import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, FileCheck, Send, XCircle, ClipboardList, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { offersAPI, downloadFile } from '../../services/api';
import { useCompany } from '../../services/companyState';
import { formatEuro } from '../../services/serviceCatalog';
import LoadingSpinner from '../Common/LoadingSpinner';

const STATUS_LABELS = {
  draft: 'Entwurf',
  sent: 'Versendet',
  accepted: 'Angenommen',
  rejected: 'Abgelehnt',
  expired: 'Abgelaufen',
};
const STATUS_CLASS = {
  draft: 'status-badge status-draft',
  sent: 'status-badge status-sent',
  accepted: 'status-badge status-paid',
  rejected: 'status-badge status-overdue',
  expired: 'status-badge status-inactive',
};

const Offers = () => {
  const company = useCompany();
  const isKleinunternehmer = company?.small_business === true;
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter]);

  const loadOffers = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        limit: 100,
      };
      const response = await offersAPI.getAll(params);
      setOffers(response.data);
    } catch (err) {
      console.error('Error loading offers:', err);
      toast.error('Fehler beim Laden der Angebote');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (offer) => {
    if (!window.confirm(`Angebot "${offer.offer_number}" wirklich löschen?`)) return;
    try {
      await offersAPI.delete(offer.id);
      toast.success('Angebot gelöscht');
      loadOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleDownloadPdf = async (offer) => {
    try {
      if (!offer.pdf_path) {
        await offersAPI.generatePDF(offer.id);
      }
      const res = await offersAPI.download(offer.id);
      downloadFile(res.data, `Angebot_${offer.offer_number}.pdf`);
    } catch {
      toast.error('PDF-Download fehlgeschlagen');
    }
  };

  const handleMark = async (offer, action) => {
    try {
      if (action === 'sent') await offersAPI.markSent(offer.id);
      if (action === 'accepted') await offersAPI.markAccepted(offer.id);
      if (action === 'rejected') await offersAPI.markRejected(offer.id);
      toast.success('Status aktualisiert');
      loadOffers();
    } catch (err) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  return (
    <div>
      <div className="content-header">
        <div>
          <h1>Angebote</h1>
          <p>Angebote erstellen und verwalten{isKleinunternehmer ? ' (§19 UStG)' : ''}</p>
        </div>
        <Link to="/offers/new" className="btn btn-primary">
          <Plus size={16} />
          Neues Angebot
        </Link>
      </div>

      <div className="content-body">
        <div className="card mb-6">
          <div className="card-body">
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <div className="relative">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Angebote suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="form-input"
                >
                  <option value="">Alle Status</option>
                  <option value="draft">Entwurf</option>
                  <option value="sent">Versendet</option>
                  <option value="accepted">Angenommen</option>
                  <option value="rejected">Abgelehnt</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="large" />
          </div>
        ) : (
          <div className="table-container">
            {offers.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Nr.</th>
                    <th>Datum</th>
                    <th>Kunde</th>
                    <th>Titel</th>
                    <th>Betrag</th>
                    <th>Status</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {offers.map((o) => (
                    <tr key={o.id}>
                      <td className="font-semibold">{o.offer_number}</td>
                      <td>{o.offer_date}</td>
                      <td>{o.customer?.name || '—'}</td>
                      <td>{o.title || <span className="text-gray-400">—</span>}</td>
                      <td>{formatEuro(o.total_amount)}</td>
                      <td>
                        <span className={STATUS_CLASS[o.status] || 'status-badge'}>
                          {STATUS_LABELS[o.status] || o.status}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <Link
                            to={`/offers/${o.id}/edit`}
                            className="btn btn-outline btn-sm"
                            title="Bearbeiten"
                          >
                            <Edit2 size={14} />
                          </Link>
                          <button
                            onClick={() => handleDownloadPdf(o)}
                            className="btn btn-outline btn-sm"
                            title="PDF herunterladen"
                          >
                            <Download size={14} />
                          </button>
                          {o.status === 'draft' && (
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => handleMark(o, 'sent')}
                              title="Als versendet markieren"
                            >
                              <Send size={14} />
                            </button>
                          )}
                          {(o.status === 'draft' || o.status === 'sent') && (
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => handleMark(o, 'accepted')}
                              title="Als angenommen markieren"
                            >
                              <FileCheck size={14} />
                            </button>
                          )}
                          {(o.status === 'draft' || o.status === 'sent') && (
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => handleMark(o, 'rejected')}
                              title="Als abgelehnt markieren"
                            >
                              <XCircle size={14} />
                            </button>
                          )}
                          {o.status !== 'accepted' && (
                            <button
                              onClick={() => handleDelete(o)}
                              className="btn btn-danger btn-sm"
                              title="Löschen"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <ClipboardList size={48} className="mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold mb-2">Keine Angebote gefunden</h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm || statusFilter
                    ? 'Keine Treffer für die Filter.'
                    : 'Es wurde noch kein Angebot erstellt.'}
                </p>
                <Link to="/offers/new" className="btn btn-primary">
                  <Plus size={16} />
                  Erstes Angebot erstellen
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Offers;
